import { useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Outlet, useMatch, useParams } from "react-router-dom";

import { queryKeys } from "~/api/queryKeys";
import { SiteWithAuth } from "~/components/siteSettings/General.tsx";
import { useCableSubscription } from "~/hooks/useCableSubscription";
import {
  refetchPendingExports,
  upsertCachedExport,
} from "~/pages/dashboard/reports/ExportsPage/exportsCache";
import { PATTERNS } from "~/router";
import { PageImportResponseWithAuth } from "~/types/dashboard";
import type { WebSocketMessage } from "~/types/websocket";
import type { CableSubscriptionParams } from "~/types/websocket";

import { SiteCableContext } from "../contexts/SiteCableContext";
import { useSiteContext } from "../contexts/SiteContext";

const TERMINAL_STATUSES = new Set(["completed", "failed"]);

const SiteWrapper = () => {
  const { id: siteId } = useParams();
  const queryClient = useQueryClient();
  const siteContext = useSiteContext();

  const isPagesList = !!useMatch(PATTERNS.sitePages);

  const [queuedCount, setQueuedCount] = useState<number>(0);

  const onConnected = useCallback(() => {
    // console.info("🌐 WS connected");
    if (siteId) void refetchPendingExports(queryClient, siteId);
  }, [queryClient, siteId]);

  const onDisconnected = useCallback(() => {
    // console.info("❌ WS disconnected, retry?", willAttemptReconnect);
  }, []);

  const onReceived = useCallback(
    async (msg: WebSocketMessage) => {
      const { action } = msg;
      // console.info(`📨 WS received: action: ${action}`, msg);

      switch (action) {
        case "crawl-status-change": {
          setQueuedCount(msg.payload.stats.queued);

          const prevStatus = siteContext?.site?.site?.crawl_status;

          // refetch site first — WebSocket messages arrive via Redis without ordering
          // guarantees, so only the API response has the authoritative crawl_status
          const result = await siteContext?.refetchSite();
          const status = result?.data?.site?.crawl_status;

          // only refetch pages, statuses, and types once crawling transitions
          // to a terminal state — ignore duplicate messages for the same status
          if (
            isPagesList &&
            status !== prevStatus &&
            TERMINAL_STATUSES.has(status ?? "")
          ) {
            void queryClient.invalidateQueries({
              queryKey: queryKeys.pagesRoot(siteId!),
            });
            void queryClient.invalidateQueries({
              queryKey: queryKeys.pageStatusesDetailRoot(siteId!),
            });
            void queryClient.invalidateQueries({
              queryKey: queryKeys.pageTypesDetailRoot(siteId!),
            });
          }

          break;
        }
        case "crawl-progress": {
          setQueuedCount(msg.payload.stats.queued);
          break;
        }
        case "page-import-status": {
          await queryClient.invalidateQueries({
            queryKey: ["siteDetails", siteId],
            exact: true,
          });

          // necessary to run after site refetch as page_import_id could be changed (reuploaded a new file)
          const siteResp = queryClient.getQueryData<SiteWithAuth>([
            "siteDetails",
            siteId,
          ]);

          // @ts-expect-error TODO: remove after site.page_import API schema would be fixed and would exclude String type
          const pageImportId = siteResp?.site.page_import?.id;

          if (pageImportId) {
            void queryClient.invalidateQueries({
              queryKey: ["pageImport", pageImportId],
              exact: true,
            });
          }

          break;
        }
        case "page-import-stats": {
          // @ts-expect-error TODO: remove after site.page_import API schema would be fixed and would exclude String type
          const pageImportId = siteContext?.site?.site?.page_import?.id;

          queryClient.setQueryData(
            ["pageImport", pageImportId],
            (prev: PageImportResponseWithAuth) => {
              if (!prev) return prev;

              return {
                ...prev,
                page_import: {
                  ...prev.page_import,
                  stats: msg.payload,
                },
              };
            },
          );

          break;
        }
        case "site-customization-actions-changed": {
          void queryClient.invalidateQueries({
            queryKey: queryKeys.siteCustomizationActions(Number(siteId)),
          });
          break;
        }
        case "export-status": {
          upsertCachedExport(queryClient, siteId!, msg.payload);
          break;
        }
        default:
          break;
      }
    },
    [isPagesList, siteContext, queryClient, siteId],
  );

  const params = useMemo<CableSubscriptionParams>(
    () => (siteId ? { id: siteId } : ({} as CableSubscriptionParams)),
    [siteId],
  );

  useCableSubscription("SitesChannel", params, {
    connected: onConnected,
    disconnected: onDisconnected,
    received: onReceived,
  });

  const siteCableContextValue = useMemo(() => ({ queuedCount }), [queuedCount]);

  useEffect(() => setQueuedCount(0), [siteId]);

  return (
    <SiteCableContext.Provider value={siteCableContextValue}>
      <Outlet />
    </SiteCableContext.Provider>
  );
};

export default SiteWrapper;
